import { useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, InputAdornment, MenuItem, Select } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// Option labels are legacy `src/locales` keys (CustomDropdown's contract), outside the rewamp namespace.
import { useTranslation } from "react-i18next";
import { dropdownPartSx } from "../../../design/recipes/dropdown";
import DropdownArrowIcon from "./DropdownArrowIcon";
import { useFilterCellAnchor } from "./FilterFieldCell";

// Single-select trigger on a dropdown variant; the listing is the theme's MuiMenu. Options keep
// CustomDropdown's `{ id, value }` contract (value = i18n key) so descriptors need no change.
export default function FilterSelectControl({
  controlId,
  ariaLabel,
  options,
  value,
  onChange,
  dropdownVariant,
  fullWidth,
  clearable,
  clearLabel,
  error,
  describedBy,
}) {
  const { t } = useTranslation();
  const { gap } = useFilterCellAnchor();
  const showClear = clearable && Boolean(value);
  const [open, setOpen] = useState(false);
  // The dropdown recipe lays the arrow out beside MUI's trigger, so clicks on it (or the frame) never reach the trigger.
  const openFromFrame = (event) => {
    if (event.button !== 0) return;
    if (event.target !== event.currentTarget && !event.target.closest?.(".MuiSelect-icon")) return;
    event.preventDefault();
    setOpen(true);
  };
  return (
    <Select
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onMouseDown={openFromFrame}
      id={controlId}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      displayEmpty
      fullWidth={fullWidth}
      error={error}
      IconComponent={DropdownArrowIcon}
      inputProps={{ "aria-label": ariaLabel, "aria-describedby": describedBy }}
      SelectDisplayProps={error ? { "aria-invalid": true } : undefined}
      endAdornment={
        showClear ? (
          <InputAdornment position="end" sx={{ mr: 3 }}>
            {/* mousedown would otherwise reach the Select and open its menu. */}
            <IconButton size="small" aria-label={clearLabel} onMouseDown={(event) => event.stopPropagation()} onClick={() => onChange("")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null
      }
      renderValue={(selected) => {
        const option = options.find((o) => o.id === selected);
        if (option) return t(option.value);
        return (
          <Box component="span" sx={{ color: "formText.placeholder" }}>
            {t("COMMON_SELECT")}
          </Box>
        );
      }}
      MenuProps={{ disablePortal: true, slotProps: { paper: { sx: { mt: gap } } } }}
      sx={dropdownPartSx(dropdownVariant, "trigger")}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {t(option.value)}
        </MenuItem>
      ))}
    </Select>
  );
}

FilterSelectControl.propTypes = {
  controlId: PropTypes.string,
  ariaLabel: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  dropdownVariant: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool,
  clearable: PropTypes.bool,
  clearLabel: PropTypes.string,
  error: PropTypes.bool,
  describedBy: PropTypes.string,
};

FilterSelectControl.defaultProps = {
  controlId: undefined,
  ariaLabel: undefined,
  options: [],
  value: "",
  fullWidth: true,
  clearable: false,
  clearLabel: undefined,
  error: false,
  describedBy: undefined,
};
